import { useState, useEffect } from 'react';

import { featuresApi, FeaturesConfig, AllPlanFeaturesConfig, NetworkConfig } from '@/lib/api/features';

// In-memory cache for features (shared across all hook instances)
let featuresCache: FeaturesConfig | null = null;
let fetchPromise: Promise<FeaturesConfig> | null = null;

// In-memory cache for plan features
let planFeaturesCache: AllPlanFeaturesConfig | null = null;
let planFeaturesFetchPromise: Promise<AllPlanFeaturesConfig> | null = null;

// In-memory cache for network config
let networkConfigCache: NetworkConfig | null = null;
let networkConfigFetchPromise: Promise<NetworkConfig> | null = null;

// Default features (used while loading or on error)
const defaultFeatures: FeaturesConfig = {
  templates: true, // Default to enabled for better dev experience
  connectors: true, // Default to enabled for better dev experience
  settingsWorkspace: true,
  settingsNotifications: true,
  settingsIntegration: true,
  createWorkspace: true,
  mediaLibrary: false,
  mediaLibraryProviders: ["pexels", "unsplash", "freesound"],
  agentEngine: "opencode",
  byoc: true, // Default to enabled; matches backend default
  optisignsAssets: false, // Off by default; enabled per env via app_configs.features
  ownershipTransfer: true, // Default to enabled; matches backend default
};

// Default plan features (restrictive defaults for safety)
const defaultPlanFeatures: AllPlanFeaturesConfig = {
  free: {
    models: ['haiku', 'sonnet'],
    visibility: ['public'],
    customDomain: false,
  },
  standard: {
    models: ['haiku', 'sonnet'],
    visibility: ['public', 'private'],
    customDomain: true,
  },
  pro: {
    models: ['haiku', 'sonnet', 'opus'],
    visibility: ['public', 'private'],
    customDomain: true,
  },
  enterprise: {
    models: ['haiku', 'sonnet', 'opus'],
    visibility: ['public', 'private'],
    customDomain: true,
  },
  // Embed workspaces — effective plan derived from subscription.status at gating site.
  // Trial users (status='trialing') get restricted access; paid users (status='active')
  // get Pro-equivalent access including Opus.
  embed_trial: {
    models: ['haiku', 'sonnet'],
    visibility: ['public', 'private'],
    customDomain: false,
  },
  embed_active: {
    models: ['haiku', 'sonnet', 'opus'],
    visibility: ['public', 'private'],
    customDomain: false,
  },
};

/**
 * Hook to access feature flags
 * Fetches once on first use and caches the result in memory
 */
export function useFeatures() {
  const [features, setFeatures] = useState<FeaturesConfig>(
    featuresCache || defaultFeatures
  );
  const [loading, setLoading] = useState(!featuresCache);

  useEffect(() => {
    // If already cached, use cached value
    if (featuresCache) {
      setFeatures(featuresCache);
      setLoading(false);
      return;
    }

    // If fetch already in progress, wait for it
    if (fetchPromise) {
      fetchPromise
        .then((data) => {
          setFeatures(data);
          setLoading(false);
        })
        .catch(() => {
          setFeatures(defaultFeatures);
          setLoading(false);
        });
      return;
    }

    // Start new fetch
    fetchPromise = featuresApi.getFeatures();
    fetchPromise
      .then((data) => {
        featuresCache = data;
        setFeatures(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch features:', error);
        setFeatures(defaultFeatures);
        setLoading(false);
      })
      .finally(() => {
        fetchPromise = null;
      });
  }, []);

  return { features, loading };
}

/**
 * Hook to access plan-based feature restrictions
 * Fetches once on first use and caches the result in memory
 */
export function usePlanFeatures() {
  const [planFeatures, setPlanFeatures] = useState<AllPlanFeaturesConfig>(
    planFeaturesCache || defaultPlanFeatures
  );
  const [loading, setLoading] = useState(!planFeaturesCache);

  useEffect(() => {
    // If already cached, use cached value
    if (planFeaturesCache) {
      setPlanFeatures(planFeaturesCache);
      setLoading(false);
      return;
    }

    // If fetch already in progress, wait for it
    if (planFeaturesFetchPromise) {
      planFeaturesFetchPromise
        .then((data) => {
          setPlanFeatures(data);
          setLoading(false);
        })
        .catch(() => {
          setPlanFeatures(defaultPlanFeatures);
          setLoading(false);
        });
      return;
    }

    // Start new fetch
    planFeaturesFetchPromise = featuresApi.getPlanFeatures();
    planFeaturesFetchPromise
      .then((data) => {
        planFeaturesCache = data;
        setPlanFeatures(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch plan features:', error);
        setPlanFeatures(defaultPlanFeatures);
        setLoading(false);
      })
      .finally(() => {
        planFeaturesFetchPromise = null;
      });
  }, []);

  return { planFeatures, loading };
}

// Default network config (empty - no IPs configured)
const defaultNetworkConfig: NetworkConfig = {
  egressIps: [],
};

/**
 * Hook to access network configuration (egress IPs for database whitelist)
 * Fetches once on first use and caches the result in memory
 */
export function useNetworkConfig() {
  const [networkConfig, setNetworkConfig] = useState<NetworkConfig>(
    networkConfigCache || defaultNetworkConfig
  );
  const [loading, setLoading] = useState(!networkConfigCache);

  useEffect(() => {
    // If already cached, use cached value
    if (networkConfigCache) {
      setNetworkConfig(networkConfigCache);
      setLoading(false);
      return;
    }

    // If fetch already in progress, wait for it
    if (networkConfigFetchPromise) {
      networkConfigFetchPromise
        .then((data) => {
          setNetworkConfig(data);
          setLoading(false);
        })
        .catch(() => {
          setNetworkConfig(defaultNetworkConfig);
          setLoading(false);
        });
      return;
    }

    // Start new fetch
    networkConfigFetchPromise = featuresApi.getNetworkConfig();
    networkConfigFetchPromise
      .then((data) => {
        networkConfigCache = data;
        setNetworkConfig(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch network config:', error);
        setNetworkConfig(defaultNetworkConfig);
        setLoading(false);
      })
      .finally(() => {
        networkConfigFetchPromise = null;
      });
  }, []);

  return { networkConfig, loading };
}
